import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
} from "@mui/material";
import RestoreIcon from "@mui/icons-material/Restore";
import Btn from "../../../Components/Ui_elements/Btn";
import GenericTable from "../../../Components/Ui_elements/GenericTable";
import { useDispatch, useSelector } from "react-redux";
import {
  getTrashItem,
  recoverTrashItem,
} from "../../../redux/Thunk/AdminModule/AdminThunk";
import { formatUTCDateToLocal } from "../../../Helper/generalhelper";
import PopupModal from "../../../Components/Ui_elements/PopupModal";
import { Grid } from "@mui/material";
import { showSnackbar } from "../../../redux/Slice/snackbar/snackbarSlice";

const Trash = () => {
  const dispatch = useDispatch();
  const [ openRestore, setOpenRestore] = useState(false)
  const [restoreId, setRestoreId] = useState({id:'', orderId:''})
  const [state, setState] = useState({
    trashType: "scoop",
    page: 1,
    pageSize: 100,
    order: { column: 0, dir: "desc" },
    search: { project_name: "" },
    error: null,
  });

  const {
    data = [],
    recordsTotal = 0,
    loading,
  } = useSelector((state) => state.root.AdminModule.trashItem || {});

  const fetchTrashItems = useCallback(() => {
    setState((prev) => ({ ...prev, error: null }));

    const payload = {
      draw: state.page,
      start: (state.page - 1) * state.pageSize,
      length: state.pageSize,
      order: [{ column: state.order.column, dir: state.order.dir }],
      search: "",
      trashType: state.trashType,
    };
    dispatch(getTrashItem(payload));
  }, [dispatch, state.page, state.pageSize, state.order, state.trashType]);

  useEffect(() => {
    fetchTrashItems();
  }, [fetchTrashItems]);

  const handleOpenPopUp = async (id, orderId) => {
    if (!id) return;

    setRestoreId({id:id, orderId:orderId})
    setOpenRestore(true)
  };

  const handleRestore = async () => {
  if (!restoreId.id) return;
  
  try {
    const res = await dispatch(
      recoverTrashItem({ 
        id: restoreId.id, 
        trashType: state.trashType, 
        order_id: restoreId?.orderId 
      })
    );
    if (res?.payload?.status === 200) {
      dispatch(
        showSnackbar({
          message: res.payload.msg,
          type: "success",
        })
      );
      setOpenRestore(false);
      fetchTrashItems();
    } else {
      dispatch(
        showSnackbar({
          message: res.payload?.msg || "Failed to restore item",
          type: "error",
        })
      );
    }
  } catch (error) {
    dispatch(
      showSnackbar({
        message: "Something went wrong",
        type: "error",
      })
    );
  }
}
  

  const columns = useMemo(() => {
    const typeSpecificColumn = {
      name: state.trashType === "scoop" ? "project_number" : "po_number",
      label: state.trashType === "scoop" ? "Project Number" : "Job Number",
    };

    return [
      {
        name: "index",
        label: "#",
        options: {
          sort: false,
          customBodyRender: (_, tableMeta) =>
            (state.page - 1) * state.pageSize + tableMeta.rowIndex + 1,
        },
      },
      typeSpecificColumn,
      {
        name: "updated_date",
        label: "Deleted Date",
        options: {
          customBodyRender: (value) => formatUTCDateToLocal(value),
        },
      },
      {
        name: "action",
        label: "Action",
        options: {
          sort: false,
          customBodyRender: (_, tableMeta) => {
            const row = data[tableMeta.rowIndex];
            const id =
              state.trashType === "scoop" ? row.itemId : row.job_summmeryId;
            return (
              <Btn
                variant="text"
                title="Restore"
                onClick={() => handleOpenPopUp(id, row.order_id)}
                startIcon={<RestoreIcon />}
              />
            );
          },
        },
      },
    ];
  }, [state.trashType, data, state.page, state.pageSize]);

  const handleTableChange = useCallback((action, tableState) => {
    setState((prev) => {
      switch (action) {
        case "changePage":
          return { ...prev, page: tableState.page + 1 };
        case "changeRowsPerPage":
          return { ...prev, pageSize: tableState.rowsPerPage, page: 1 };
        case "sort":
          return {
            ...prev,
            order: {
              column: tableState.sortOrder.index,
              dir: tableState.sortOrder.direction,
            },
          };
        default:
          return prev;
      }
    });
  }, []);

  const handleTabChange = (_, newValue) => {
    setState((prev) => ({ ...prev, trashType: newValue, page: 1 }));
  };

  return (
    <Box sx={{ p: 2, width: "70%" }}>
      <Typography variant="h4" sx={{ mb: 2 }}>
        Trash Item
      </Typography>

      <Tabs value={state.trashType} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="Project Scoop" value="scoop" />
        <Tab label="Job" value="job" />
      </Tabs>

      {state.error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.error.message || "Failed to load data."}
        </Alert>
      )}

      <Card>
        <CardContent sx={{ p: "0px !important" }}>
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <GenericTable
              columns={columns}
              data={data}
              showAction={false}
              options={{
                serverSide: true,
                count: recordsTotal,
                page: state.page - 1,
                rowsPerPage: state.pageSize,
                rowsPerPageOptions: [10, 25, 50, 100],
                search: false,
                download: false,
                print: false,
                viewColumns: false,
                sortOrder: {
                  index: state.order.column,
                  direction: state.order.dir,
                },
                onTableChange: handleTableChange,
              }}
            />
          )}
        </CardContent>
      </Card>
      {openRestore && (
        <PopupModal
          maxWidth={"sm"}
          fullWidth={true}
          open={openRestore}
          handleClose={() => setOpenRestore(false)}
          actions={
            <Grid container columnGap={2} justifyContent={"end"}>
              <Btn onClick={handleRestore}>
                Yes
              </Btn>
              <Btn onClick={() => setOpenRestore(false)}>Cancel</Btn>
            </Grid>
          }
          title={<Typography variant="h6">Restore</Typography>}
        >
          <Typography variant="body2" >Are you sure want to restore?</Typography>
        </PopupModal>
      )}
    </Box>
  );
};

export default Trash;
